import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Play, ExternalLink } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { API, BACKEND_URL } from "../config";
import Footer from "./components/Footer";
import { useMusicPlayer } from "../contexts/AppContext";
import { useSettings } from "../contexts/AppContext";
import { useFanClub } from "../contexts/AppContext";

const WELCOME_STORAGE_KEY = "homepageWelcomeSeen";
const WELCOME_VISIBLE_MS = 2200;
const WELCOME_FADEOUT_MS = 600;

export default function HomePage() {
  const [albums, setAlbums] = useState([]);
  const [tourDates, setTourDates] = useState([]);
  const [email, setEmail] = useState('');
  const [welcomePhase, setWelcomePhase] = useState(() =>
    typeof window !== "undefined" && !sessionStorage.getItem(WELCOME_STORAGE_KEY) ? "show" : "done"
  );
  const welcomeDoneRef = useRef(false);
  const { playTrack } = useMusicPlayer();
  const { concerts_enabled } = useSettings();
  const { member } = useFanClub();

  useEffect(() => {
    if (welcomeDoneRef.current) return;
    const seen = sessionStorage.getItem(WELCOME_STORAGE_KEY);
    if (seen) {
      setWelcomePhase("done");
      return;
    }
    setWelcomePhase("show");
    const showTimer = setTimeout(() => {
      setWelcomePhase("leave");
      sessionStorage.setItem(WELCOME_STORAGE_KEY, "1");
      welcomeDoneRef.current = true;
      setTimeout(() => setWelcomePhase("done"), WELCOME_FADEOUT_MS);
    }, WELCOME_VISIBLE_MS);
    return () => clearTimeout(showTimer);
  }, []);

  const showWelcomeOverlay = welcomePhase === "show" || welcomePhase === "leave";

  useEffect(() => {
    axios.get(`${API}/albums`).then(res => setAlbums(res.data)).catch(console.error);
    const tourHeaders = member?.api_token ? { headers: { Authorization: `Bearer ${member.api_token}` } } : {};
    axios.get(`${API}/tour-dates`, tourHeaders).then(res => setTourDates(res.data.slice(0, 3))).catch(console.error);
  }, [member?.api_token]);

  const handleNewsletter = async (e) => {
    e.preventDefault();
    try { await axios.post(`${API}/newsletter`, { email }); toast.success('Sikeresen feliratkoztál!'); setEmail(''); }
    catch (error) { toast.error('Hiba történt'); }
  };

  const latestAlbum = albums[0];

  return (
    <div data-testid="home-page" className="relative">
      {/* Első látogatás – üdvözlő overlay csak a főoldalon, egyszer per session */}
      {showWelcomeOverlay && (
        <div
          className={`fixed inset-0 z-[100] flex flex-col items-center justify-center bg-[#080808]/95 backdrop-blur-sm ${
            welcomePhase === "show" ? "home-welcome-enter" : "home-welcome-leave"
          }`}
          aria-hidden="true"
        >
          <p className="home-hero-line text-sm sm:text-base tracking-[0.25em] text-primary uppercase mb-4 font-medium" style={{ animationDelay: "0.2s", animationFillMode: "forwards" }}>
            Pop Művész
          </p>
          <h2 className="home-hero-line text-3xl sm:text-4xl md:text-5xl font-bold font-['Syne'] tracking-tight text-white mb-4" style={{ animationDelay: "0.4s", animationFillMode: "forwards" }}>
            Tajti Zoltán
          </h2>
          <p className="home-hero-line text-lg sm:text-xl md:text-2xl text-white/80 max-w-md text-center font-['Syne']" style={{ animationDelay: "0.65s", animationFillMode: "forwards" }}>
            Sötét dallamok az éjféli lelkeknek
          </p>
        </div>
      )}

      <section className="min-h-screen pt-20 sm:pt-24 px-4 sm:px-6 md:px-12 lg:px-24 pb-8">
        <div className="hero-glow -top-20 -left-20 hidden md:block" />
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6 auto-rows-[minmax(140px,auto)] md:auto-rows-[minmax(180px,auto)] relative">
          <div className="md:col-span-8 md:row-span-2 relative rounded-xl sm:rounded-2xl overflow-hidden group animate-fade-in min-h-[280px] sm:min-h-[360px]">
            <img src="./assets/images/mainPicture.png" alt="Tajti Zoltán" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/50 to-transparent" />
            <div className="absolute bottom-0 left-0 p-5 sm:p-8 md:p-12">
              <p className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-primary mb-2 sm:mb-4 font-medium home-hero-line" style={{ animationDelay: "0.1s", animationFillMode: "forwards" }}>Pop Művész</p>
              <h1 className="text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-bold font-['Syne'] tracking-tighter leading-none mb-2 sm:mb-4 home-hero-line" style={{ animationDelay: "0.25s", animationFillMode: "forwards" }}>Tajti<br/>Zoltán</h1>
              <p className="text-sm sm:text-base md:text-lg text-white/70 max-w-md mb-4 sm:mb-6 home-hero-line" style={{ animationDelay: "0.4s", animationFillMode: "forwards" }}>Sötét dallamok az éjféli lelkeknek</p>
              <Link to="/zene" className="home-hero-line block" style={{ animationDelay: "0.55s", animationFillMode: "forwards" }}><Button className="rounded-full px-5 sm:px-8 py-4 sm:py-6 text-sm sm:text-lg font-bold bg-primary hover:bg-primary/90 animate-pulse-glow">Hallgasd meg</Button></Link>
            </div>
          </div>

          {latestAlbum && (
            <div className="md:col-span-4 md:row-span-2 bg-card rounded-xl sm:rounded-2xl overflow-hidden border border-white/5 animate-fade-in stagger-1">
              <div className="relative aspect-[4/3] sm:aspect-square">
                <img src={`${BACKEND_URL}/storage/${latestAlbum.cover_image}`} alt={latestAlbum.title} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-card to-transparent" />
                <Button size="icon" className="absolute bottom-3 right-3 sm:bottom-4 sm:right-4 w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-primary hover:bg-primary/90 shadow-lg"
                  onClick={() => playTrack(latestAlbum.tracks[0], latestAlbum, latestAlbum.tracks)}><Play className="h-5 w-5 sm:h-6 sm:w-6 ml-0.5" /></Button>
              </div>
              <div className="p-4 sm:p-6">
                <p className="text-[10px] sm:text-xs uppercase tracking-[0.15em] text-primary mb-1 sm:mb-2">Legújabb megjelenés</p>
                <h3 className="text-lg sm:text-2xl font-bold font-['Syne'] mb-1 sm:mb-2">{latestAlbum.title}</h3>
                <p className="text-muted-foreground text-xs sm:text-sm">{latestAlbum.tracks.length} szám • {latestAlbum.release_year}</p>
              </div>
            </div>
          )}

          {concerts_enabled && (
          <div className="md:col-span-6 bg-card rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 border border-white/5 animate-fade-in stagger-2">
            <div className="flex justify-between items-start mb-4 sm:mb-6">
              <div>
                <p className="text-[10px] sm:text-xs uppercase tracking-[0.15em] text-accent mb-1 sm:mb-2">Turné</p>
                <h3 className="text-lg sm:text-2xl font-bold font-['Syne']">Közelgő koncertek</h3>
              </div>
              <Link to="/koncertek"><Button variant="ghost" className="text-primary hover:text-primary text-xs sm:text-sm p-2">Összes<ExternalLink className="h-3 w-3 ml-1" /></Button></Link>
            </div>
            <div className="space-y-3">
              {tourDates.map((date) => (
                <div key={date.id} className="flex justify-between items-center py-2 border-b border-white/5 last:border-0">
                  <div><p className="font-medium text-sm">{date.city}</p><p className="text-xs text-muted-foreground">{date.venue}</p></div>
                  <div className="text-right">
                    <p className="font-medium text-sm">{new Date(date.date).toLocaleDateString('hu-HU', { month: 'short', day: 'numeric' })}</p>
                    <div className="flex flex-wrap gap-1 justify-end">
                      {date.sold_out && <Badge className="bg-accent/20 text-accent text-[10px]">Elfogyott</Badge>}
                      {date.is_private && <Badge className="bg-primary/20 text-primary text-[10px]">Zártkörű</Badge>}
                      {date.is_free && <Badge className="bg-green-500/20 text-green-400 text-[10px]">Ingyenes</Badge>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          )}

          <div className={`${concerts_enabled ? 'md:col-span-6' : 'md:col-span-12'} bg-gradient-to-br from-primary/20 to-accent/10 rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 border border-primary/20 animate-fade-in stagger-3`}>
            <p className="text-[10px] sm:text-xs uppercase tracking-[0.15em] text-primary mb-1 sm:mb-2">Maradj naprakész</p>
            <h3 className="text-lg sm:text-2xl font-bold font-['Syne'] mb-2 sm:mb-4">Hírlevél</h3>
            <p className="text-muted-foreground text-xs sm:text-base mb-4">Légy az első, aki értesül az új megjelenésekről!</p>
            <form onSubmit={handleNewsletter} className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <Input type="email" placeholder="Email címed" value={email} onChange={(e) => setEmail(e.target.value)} className="flex-1 bg-black/40 border-white/10 h-10 sm:h-12 rounded-full px-4 sm:px-6 text-sm" />
              <Button type="submit" className="rounded-full px-5 sm:px-6 h-10 sm:h-12 bg-primary hover:bg-primary/90 text-sm">Feliratkozás</Button>
            </form>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
